import React from "react";
import {
  ShieldCheck,
  ScrollText,
  BadgeCheck,
  HeartHandshake,
  Scale,
  Compass,
} from "lucide-react";
import SEO from "../components/SEO";
import Breadcrumbs from "../components/Breadcrumbs";
import CtaBand from "../components/CtaBand";
import { legal } from "../data/legal";

const values = [
  {
    icon: ScrollText,
    title: "Transparent fees",
    body: "You get our support fee in writing before any work begins. It is always kept separate from the registered migration agent's fee, government charges, and third-party costs, so you know exactly who you are paying and what for.",
  },
  {
    icon: BadgeCheck,
    title: "Prepared properly",
    body: "We turn the paperwork mountain into a clear, checked, submission-ready file. Careful preparation the first time, so nothing stalls when it reaches a registered migration agent.",
  },
  {
    icon: ShieldCheck,
    title: "Matched with a registered agent",
    body: "We match you with a registered migration agent, bound by the Code of Conduct and professionally insured, who gives the regulated advice and lodges your application. We choose to work only with agents who meet that bar.",
  },
  {
    icon: HeartHandshake,
    title: "On your side",
    body: "We are your single point of contact and your people on the ground. We keep you informed, keep the matter moving, and stay beside you the whole way. Migration decisions shape lives, and we never forget that.",
  },
];

const standards = [
  {
    icon: Scale,
    title: "The registered agent advises and lodges",
    body: "A registered migration agent gives the migration advice, assesses your eligibility, recommends the visa, and lodges your application. They are bound by the Migration Agents Code of Conduct.",
  },
  {
    icon: ScrollText,
    title: "We prepare, match, and coordinate",
    body: "We get your documentation application-ready, match you with a registered migration agent, act as your single point of contact, and keep your matter moving so no one goes quiet on you.",
  },
  {
    icon: Compass,
    title: "The Department decides",
    body: "The Department of Home Affairs, not us and not the agent, decides every visa. No outcome is guaranteed, and we never claim otherwise.",
  },
];

export default function About() {
  return (
    <>
      <SEO
        title="About us"
        description={`${legal.brandName} is a migration support and coordination service. We get you application-ready, match you with a registered migration agent for the regulated advice and lodgement, and stay beside you the whole way.`}
        path="/about/"
      />
      <Breadcrumbs items={[{ label: "Home", to: "/" }, { label: "About" }]} />

      {/* Intro */}
      <section className="container-page py-12">
        <p className="eyebrow">About us</p>
        <h1 className="mt-3 max-w-3xl text-4xl font-bold text-acblue sm:text-5xl">
          Choosing migration help shouldn't feel like a gamble
        </h1>
        <p className="mt-5 max-w-2xl text-lg text-acBlack/70">
          {legal.brandName} is a migration support and coordination service. We
          help individuals, families, and employers get application-ready, we
          match you with a registered migration agent who gives the regulated
          advice and lodges your application, and we stay involved to keep your
          matter moving.
        </p>
      </section>

      {/* Why we exist */}
      <section className="bg-acBg py-16">
        <div className="container-page grid gap-10 lg:grid-cols-3">
          <div>
            <p className="eyebrow">Why we exist</p>
            <h2 className="mt-3 text-3xl font-bold text-acblue">
              Trust, in an industry that needs more of it
            </h2>
          </div>
          <div className="space-y-4 text-acBlack/75 lg:col-span-2">
            <p>
              Migration is high stakes and, sadly, it attracts operators who
              overpromise, hide their fees, or imply influence they do not have.
              That makes choosing an adviser stressful at exactly the moment you
              need confidence.
            </p>
            <p>
              We built this service around the opposite approach. We say plainly
              what we do and what we do not, we put our fee in writing before you
              commit, and we put you in the hands of a registered, accountable,
              insured migration agent for the regulated work. It is a simpler
              promise than the hype you may have seen elsewhere, and it is one we
              can keep.
            </p>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="container-page py-16">
        <div className="mx-auto max-w-2xl text-center">
          <p className="eyebrow">What we stand for</p>
          <h2 className="mt-3 text-3xl font-bold text-acblue sm:text-4xl">
            Four things we will always do
          </h2>
        </div>
        <div className="mt-12 grid gap-6 sm:grid-cols-2">
          {values.map((v) => (
            <div key={v.title} className="card">
              <v.icon className="h-9 w-9 text-acAccent" />
              <h3 className="mt-4 text-lg font-semibold text-acblue">
                {v.title}
              </h3>
              <p className="mt-2 text-sm text-acBlack/70">{v.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Standards */}
      <section className="bg-acblue py-16 text-white">
        <div className="container-page">
          <div className="mx-auto max-w-2xl text-center">
            <p className="text-xs font-semibold uppercase tracking-widest text-acGold">
              Who does what
            </p>
            <h2 className="mt-3 text-3xl font-bold sm:text-4xl">
              Clear roles, so you always know who is doing what
            </h2>
          </div>
          <div className="mt-12 grid gap-6 sm:grid-cols-3">
            {standards.map((s) => (
              <div
                key={s.title}
                className="rounded-3xl bg-white/5 p-6 ring-1 ring-white/10"
              >
                <s.icon className="h-9 w-9 text-acGold" />
                <h3 className="mt-4 text-lg font-semibold">{s.title}</h3>
                <p className="mt-2 text-sm text-white/70">{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <CtaBand
        heading="Get application-ready, matched, and supported"
        body="Book a consultation to talk through your situation. We get your file ready and match you with a registered migration agent for the regulated advice and lodgement."
      />
    </>
  );
}
